import React, { useRef, useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
export type FaqAccent = 'primary' | 'violet' | 'amber';
interface FaqItem {
  q: string;
  a: string;
}
interface FaqSectionProps {
  items?: FaqItem[];
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  accent?: FaqAccent;
}
const ACCENT: Record<FaqAccent, {
  text: string;
  ring: string;
  icon: string;
}> = {
  primary: {
    text: 'text-primary-600',
    ring: 'focus-visible:ring-primary-500/40',
    icon: 'text-primary-500'
  },
  violet: {
    text: 'text-violet-600',
    ring: 'focus-visible:ring-violet-500/40',
    icon: 'text-violet-500'
  },
  amber: {
    text: 'text-amber-600',
    ring: 'focus-visible:ring-amber-500/40',
    icon: 'text-amber-500'
  }
};
export function FaqSection({
  items,
  eyebrow,
  title,
  subtitle,
  accent = 'primary'
}: FaqSectionProps) {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const [open, setOpen] = useState<number | null>(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const faqItems = items ?? t.faq.items;
  const ac = ACCENT[accent];
  return <section className="relative py-20 lg:py-24 bg-navy-50/60" id="faq" data-id="element-204" data-mp="1oj17rb7iyoty">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-205" data-mp="1oldxiajiy3hk">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }} className="text-center mb-12" data-id="element-206" data-mp="1jjom2tse3nsy">
          <span className={`text-sm font-semibold tracking-wide uppercase ${ac.text}`} data-id="element-207" data-mp="1sj1udj4s26c3">
            {eyebrow ?? t.faq.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-208" data-mp="3udssoqxfld3g">
            {title ?? t.faq.title}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-navy-500 leading-relaxed" data-id="element-209" data-mp="237xy3c01iara">
            {subtitle ?? t.faq.subtitle}
          </p>
        </motion.div>

        <motion.dl initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: [0.23, 1, 0.32, 1]
      }} className="divide-y divide-navy-200/70 border-y border-navy-200/70" data-id="element-210" data-mp="285zgrvtk0828">
          {faqItems.map((item, i) => {
          const isOpen = open === i;
          return <div key={item.q} data-id="element-211" data-mp="2vwvef9koxxhe" data-mpi={i}>
                <dt data-id="element-212" data-mp="2vvtv6htiu4ol">
                  <button type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className={`w-full flex items-start justify-between gap-6 py-5 text-left group focus:outline-none focus-visible:ring-2 rounded-lg ${ac.ring}`} data-id="element-213" data-mp="2vwc0s522pv96">
                    <span className="text-base sm:text-lg font-semibold text-navy-900 leading-snug" data-id="element-214" data-mp="obdaloobpr2f">
                      {item.q}
                    </span>
                    <PlusIcon className={`flex-shrink-0 w-5 h-5 mt-0.5 transition-transform duration-200 ease-out ${ac.icon} ${isOpen ? 'rotate-45' : ''}`} data-id="element-215" data-mp="k9rw5yw554q4" />
                  </button>
                </dt>
                <AnimatePresence initial={false} data-id="element-216" data-mp="17zw1caoz9cj8">
                  {isOpen && <motion.dd initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-217" data-mp="1372unh9y7u4i">
                      <p className="pb-6 pr-11 text-sm sm:text-base text-navy-600 leading-relaxed" data-id="element-218" data-mp="1aksew03kr8d3">
                        {item.a}
                      </p>
                    </motion.dd>}
                </AnimatePresence>
              </div>;
        })}
        </motion.dl>
      </div>
    </section>;
}